class RegistrationLookup extends HTMLElement {
  constructor() {
    super();
    this.vehicleFound = false; // Track if vehicle has been found
    this.currentVehicleMatch = null; // Store current vehicle match for side changes
    this.currentRegistration = null; // Store registration number when lookup is used
    this.currentVehicleData = null; // Store vehicle data from API response
    this.settings = null; // Will store settings from JSON
    this.vehicleData = []; // Will be populated from Shopify API
    this.isDataLoaded = false; // Track if vehicle data has been loaded
    this.isLoading = false; // Track if currently loading data
    
    // Cache settings
    this.CACHE_KEY = 'signsdiy_vehicle_data';
    this.CACHE_EXPIRY_KEY = 'signsdiy_vehicle_data_expiry';
    this.CACHE_DURATION = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds
    
    // Shopify Storefront API settings - will be set from data attributes
    this.STOREFRONT_API_URL = null;
    this.STOREFRONT_ACCESS_TOKEN = null;
  }

  // Load vehicle data from cache or API
  async loadVehicleData() {
    if (this.isDataLoaded || this.isLoading) {
      return this.vehicleData;
    }

    this.isLoading = true;
    this.setLoadingState(true);

    try {
      // Try to load from cache first
      const cachedData = this.getCachedData();
      if (cachedData) {
        console.log('Loading vehicle data from cache');
        this.vehicleData = cachedData;
        this.isDataLoaded = true;
        this.setLoadingState(false);
        return this.vehicleData;
      }

      // If no cache, fetch from API
      console.log('Fetching vehicle data from Shopify API');
      const apiData = await this.fetchVehicleDataFromAPI();
      
      if (apiData && apiData.length > 0) {
        this.vehicleData = apiData;
        this.setCachedData(apiData);
        this.isDataLoaded = true;
        console.log(`Loaded ${apiData.length} vehicles from API`);
      } else {
        throw new Error('No vehicle data received from API');
      }
    } catch (error) {
      console.error('Failed to load vehicle data:', error);
      // Fallback to empty array or show error message
      this.vehicleData = [];
      this.showMessage('Failed to load vehicle data. Please refresh the page.', 'error');
    } finally {
      this.isLoading = false;
      this.setLoadingState(false);
    }

    return this.vehicleData;
  }

  // Get cached vehicle data if not expired
  getCachedData() {
    try {
      const cachedData = localStorage.getItem(this.CACHE_KEY);
      const cacheExpiry = localStorage.getItem(this.CACHE_EXPIRY_KEY);
      
      if (cachedData && cacheExpiry) {
        const expiryTime = parseInt(cacheExpiry);
        const now = Date.now();
        
        if (now < expiryTime) {
          return JSON.parse(cachedData);
        } else {
          // Cache expired, remove it
          localStorage.removeItem(this.CACHE_KEY);
          localStorage.removeItem(this.CACHE_EXPIRY_KEY);
        }
      }
    } catch (error) {
      console.error('Error reading cached data:', error);
      // Clear corrupted cache
      localStorage.removeItem(this.CACHE_KEY);
      localStorage.removeItem(this.CACHE_EXPIRY_KEY);
    }
    
    return null;
  }

  // Cache vehicle data with expiry
  setCachedData(data) {
    try {
      const expiryTime = Date.now() + this.CACHE_DURATION;
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(this.CACHE_EXPIRY_KEY, expiryTime.toString());
      console.log('Vehicle data cached successfully');
    } catch (error) {
      console.error('Error caching data:', error);
    }
  }

  // Fetch vehicle data from Shopify Storefront API with pagination
  async fetchVehicleDataFromAPI() {
    const query = `
      query GetVehicles($first: Int!, $after: String) {
        metaobjects(type: "vehicle", first: $first, after: $after) {
          edges {
            node {
              id
              handle
              fields {
                key
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    let allVehicles = [];
    let hasNextPage = true;
    let cursor = null;
    let pageCount = 0;
    const maxPages = 20; // Safety limit to prevent infinite loops
    const pageSize = 250; // Fetch 250 vehicles per page

    try {
      while (hasNextPage && pageCount < maxPages) {
        pageCount++;
        console.log(`Fetching vehicles page ${pageCount}...`);

        const variables = {
          first: pageSize,
          ...(cursor && { after: cursor })
        };

        const response = await fetch(this.STOREFRONT_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.STOREFRONT_ACCESS_TOKEN
          },
          body: JSON.stringify({
            query: query,
            variables: variables
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        const metaobjects = data.data.metaobjects;
        const pageVehicles = this.transformAPIData(metaobjects.edges);
        allVehicles = allVehicles.concat(pageVehicles);

        // Update pagination info
        hasNextPage = metaobjects.pageInfo.hasNextPage;
        cursor = metaobjects.pageInfo.endCursor;

        console.log(`Page ${pageCount}: Fetched ${pageVehicles.length} vehicles (Total: ${allVehicles.length})`);

        // Update loading progress in UI
        if (hasNextPage) {
          this.updateLoadingProgress(pageCount + 1, allVehicles.length);
          // Small delay between requests to be respectful to the API
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      if (pageCount >= maxPages && hasNextPage) {
        console.warn(`Reached maximum page limit (${maxPages}). Some vehicles may not have been fetched.`);
      }

      console.log(`Completed fetching ${allVehicles.length} vehicles across ${pageCount} pages`);
      
      return allVehicles;

    } catch (error) {
      console.error('API fetch error:', error);
      throw error;
    }
  }

  // Transform API response to match expected data structure
  transformAPIData(edges) {
    const vehicles = [];
    
    edges.forEach(edge => {
      const node = edge.node;
      const fields = {};
      
      // Convert fields array to object
      node.fields.forEach(field => {
        fields[field.key] = field.value;
      });

      // Parse the data according to our metaobject structure
      try {
        const vehicle = {
          make: fields.make || '',
          range: fields.model || '', // Note: using 'model' field from metaobject
          years: fields.year ? JSON.parse(fields.year) : [],
          wheelbase: fields.wheelbase_type ? JSON.parse(fields.wheelbase_type)[0] : '',
          variant: fields.variant ? JSON.parse(fields.variant)[0] : ''
        };

        // Only add if we have required fields
        if (vehicle.make && vehicle.range && vehicle.years.length > 0) {
          vehicles.push(vehicle);
        }
      } catch (error) {
        console.error('Error parsing vehicle data:', node, error);
      }
    });

    console.log(`Transformed ${vehicles.length} vehicles from API data`);
    return vehicles;
  }

  // Set loading state for all selects
  setLoadingState(isLoading, progressMessage = null) {
    const selects = this.querySelectorAll('.manual-select');
    const makeSelect = this.querySelector('#make-select');
    
    selects.forEach(select => {
      select.disabled = isLoading;
    });

    if (makeSelect) {
      if (isLoading) {
        const firstOption = makeSelect.querySelector('option');
        makeSelect.innerHTML = '';
        makeSelect.appendChild(firstOption);
        firstOption.textContent = progressMessage || 'Loading vehicles...';
      } else {
        const firstOption = makeSelect.querySelector('option');
        if (firstOption) {
          firstOption.textContent = 'Select Make';
        }
      }
    }
  }

  // Update loading progress during pagination
  updateLoadingProgress(pageCount, totalVehicles) {
    const progressMessage = `Loading vehicles... (Page ${pageCount}, ${totalVehicles} loaded)`;
    this.setLoadingState(true, progressMessage);
  }

  connectedCallback() {
    console.log('RegistrationLookup connected to DOM');
    this.init();
  }



  async init() {
    console.log('Initializing RegistrationLookup...');
    this.initializeAPISettings();
    this.loadSettings();
    
    // Load vehicle data first, then populate make select
    await this.loadVehicleData();
    this.populateMakeSelect();
    this.setupEventListeners();
  }

  initializeAPISettings() {
    // Get API settings from data attributes
    const accessToken = this.dataset.storefrontAccessToken;
    const apiVersion = this.dataset.storefrontApiVersion || '2025-10';
    
    if (!accessToken) {
      console.error('Storefront Access Token not found. Please configure it in theme settings.');
      this.showMessage('Vehicle lookup not configured. Please contact support.', 'error');
      return;
    }

    this.STOREFRONT_ACCESS_TOKEN = accessToken;
    this.STOREFRONT_API_URL = `https://znasir-5f.myshopify.com/api/${apiVersion}/graphql.json`;
    
    console.log('API Settings initialized:', {
      version: apiVersion,
      url: this.STOREFRONT_API_URL,
      hasToken: !!accessToken
    });
  }

  loadSettings() {
    const settingsScript = document.getElementById('registration-lookup-settings');
    if (settingsScript) {
      try {
        this.settings = JSON.parse(settingsScript.textContent);
        console.log('Settings loaded:', this.settings);
      } catch (error) {
        console.error('Failed to parse settings:', error);
        this.settings = {
          success_message: "Vehicle found: [year] [make] [range] ([wheelbase])",
          error_message: "Failed to lookup registration. Please try again or use manual selection.",
          no_match_message: "No matching vehicle found in our database. Please use manual selection.",
          variants: []
        };
      }
    } else {
      console.warn('Settings script not found, using defaults');
      this.settings = {
        success_message: "Vehicle found: [year] [make] [range] ([wheelbase])",
        error_message: "Failed to lookup registration. Please try again or use manual selection.",
        no_match_message: "No matching vehicle found in our database. Please use manual selection.",
        variants: []
      };
    }
  }

  replaceMessageVariables(message, vehicleData) {
    if (!vehicleData) return message;

    return message
      .replace(/\[year\]/g, vehicleData.year || '')
      .replace(/\[make\]/g, vehicleData.make || '')
      .replace(/\[range\]/g, vehicleData.range || '');
  }

  findMatchingVariant(vehicleMatch) {
    if (!vehicleMatch || !vehicleMatch.variant) return null;

    const selectedSide = this.getSelectedSide();
    const expectedTitle = `${vehicleMatch.variant} / ${selectedSide}`;

    const variant = this.settings.variants.find(v =>
      v.title.toLowerCase() === expectedTitle.toLowerCase()
    );

    console.log('Looking for variant with vehicle size:', vehicleMatch.variant);
    console.log('Selected side:', selectedSide);
    console.log('Expected variant title:', expectedTitle);
    console.log('Available variants:', this.settings.variants.map(v => v.title));
    console.log('Found variant:', variant);

    return variant;
  }

  updatePriceDisplay(variant) {
    const priceSection = this.querySelector('#registration-price');
    const priceElement = this.querySelector('#variant-price');

    if (!variant || !priceSection || !priceElement) return;

    // Format price (assuming it's in cents)
    const formattedPrice = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(variant.price / 100);

    priceElement.textContent = formattedPrice;
    priceSection.style.display = 'block';
  }

  updateSideOptionPrices(vehicleMatch) {
    if (!vehicleMatch || !vehicleMatch.variant) {
      this.resetSideOptionPrices();
      return;
    }

    const _sideOptions = this.querySelectorAll('.side-option[data-side]');

    _sideOptions.forEach(_option => {
      const _sideValue = _option.dataset.side;
      const _priceElement = _option.querySelector('.side-option__price');

      if (!_priceElement) return;

      // Build expected variant title for this side option
      const _expectedTitle = `${vehicleMatch.variant} / ${_sideValue}`;
      const _variant = this.settings.variants.find(v =>
        v.title.toLowerCase() === _expectedTitle.toLowerCase()
      );

      if (_variant) {
        const _formattedPrice = new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP'
        }).format(_variant.price / 100);

        _priceElement.textContent = `${_formattedPrice}`;
      } else {
        // No variant found for this side, reset to default
        const _defaultPrice = _priceElement.dataset.defaultPrice;
        if (_defaultPrice) {
          _priceElement.textContent = _defaultPrice;
        }
      }
    });
  }

  resetSideOptionPrices() {
    const _sideOptions = this.querySelectorAll('.side-option[data-side]');

    _sideOptions.forEach(_option => {
      const _priceElement = _option.querySelector('.side-option__price');
      if (_priceElement) {
        const _defaultPrice = _priceElement.dataset.defaultPrice;
        if (_defaultPrice) {
          _priceElement.textContent = _defaultPrice;
        }
      }
    });
  }

  updateVariantId(variantId) {
    // Find the product variant ID input within the current Shopify section
    const section = this.closest('.shopify-section');
    const variantIdInput = section ? section.querySelector('.product-variant-id') : null;

    if (variantIdInput) {
      variantIdInput.value = variantId;
      console.log('Updated variant ID to:', variantId);
    } else {
      console.warn('Could not find .product-variant-id element in current Shopify section');
    }
  }

  setupEventListeners() {
    console.log('Setting up event listeners...');

    // Side selection change
    const sideRadios = this.querySelectorAll('input[name="side"]');
    sideRadios.forEach(radio => {
      radio.addEventListener('change', this.handleSideChange.bind(this));
    });
    console.log('Side selection listeners added');

    // Make select change
    const makeSelect = this.querySelector('#make-select');
    console.log('Make select found:', makeSelect);

    if (makeSelect) {
      makeSelect.addEventListener('change', (event) => {
        this.handleMakeChange(event);
      });
      console.log('Make select listener added successfully');
    } else {
      console.error('ERROR: Make select not found!');
    }



    // Model select change
    const modelSelect = this.querySelector('#model-select');
    if (modelSelect) {
      modelSelect.addEventListener('change', this.handleModelChange.bind(this));
      console.log('Model select listener added');
    }

    // Year select change
    const yearSelect = this.querySelector('#year-select');
    if (yearSelect) {
      yearSelect.addEventListener('change', this.handleYearChange.bind(this));
      console.log('Year select listener added');
    }

    // Wheelbase select change
    const wheelbaseSelect = this.querySelector('#wheelbase-select');
    if (wheelbaseSelect) {
      wheelbaseSelect.addEventListener('change', this.handleWheelbaseChange.bind(this));
      console.log('Wheelbase select listener added');
    }
  }



  handleSideChange(event) {
    console.log('Side selection changed:', event.target.value);

    // If a vehicle is already found, update the variant for the new side selection
    if (this.vehicleFound && this.currentVehicleMatch) {
      console.log('Vehicle already selected, updating variant for new side selection');

      // Find matching variant with the new side selection
      const variant = this.findMatchingVariant(this.currentVehicleMatch);
      if (variant) {
        this.updatePriceDisplay(variant);
        this.updateVariantId(variant.id);
        console.log('Updated to variant:', variant.title);
      } else {
        console.warn('No matching variant found for new side selection');
        // Hide price if no variant found
        const priceSection = this.querySelector('#registration-price');
        if (priceSection) {
          priceSection.style.display = 'none';
        }
      }
    } else {
      // No vehicle selected yet, just hide price display
      const priceSection = this.querySelector('#registration-price');
      if (priceSection) {
        priceSection.style.display = 'none';
      }
    }
  }

  getSelectedSide() {
    const selectedSideRadio = this.querySelector('input[name="side"]:checked');
    return selectedSideRadio ? selectedSideRadio.value : 'Sides & Back'; // Default fallback
  }



  populateMakeSelect() {
    const makeSelect = this.querySelector('#make-select');
    if (!makeSelect) return;

    // Get unique makes from the data
    const uniqueMakes = [...new Set(this.vehicleData.map(item => item.make))];

    // Clear existing options except the first one
    const firstOption = makeSelect.querySelector('option');
    makeSelect.innerHTML = '';
    makeSelect.appendChild(firstOption);

    // Add make options
    uniqueMakes.forEach(make => {
      const option = document.createElement('option');
      option.value = make.toLowerCase();
      option.textContent = make;
      makeSelect.appendChild(option);
    });
  }

  toTitleCase(str) {
    return str.replace(/\w\S*/g, (txt) =>
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }

  handleMakeChange(event) {

    const selectedMakeValue = event.target.value;
    const selectedMake = this.toTitleCase(selectedMakeValue);
    const modelGroup = this.querySelector('#model-group');
    const yearGroup = this.querySelector('#year-group');
    const wheelbaseGroup = this.querySelector('#wheelbase-group');
    const modelSelect = this.querySelector('#model-select');
    const yearSelect = this.querySelector('#year-select');
    const wheelbaseSelect = this.querySelector('#wheelbase-select');

    // Reset vehicle found state when make changes
    this.vehicleFound = false;
    this.currentVehicleMatch = null; // Clear stored match
    this.currentRegistration = null; // Clear registration number
    this.currentVehicleData = null; // Clear vehicle data
    this.resetSideOptionPrices(); // Reset side option prices to default

    if (selectedMakeValue) {
      // Get unique models (ranges) for the selected make
      const modelsForMake = [...new Set(
        this.vehicleData
          .filter(item => item.make === selectedMake)
          .map(item => item.range)
      )];


      // Show model select and populate options
      this.populateSelectWithStrings(modelSelect, modelsForMake);
      this.showElement(modelGroup);

      // Hide subsequent selects and reset
      this.hideElement(yearGroup);
      this.hideElement(wheelbaseGroup);
      yearSelect.value = '';
      wheelbaseSelect.value = '';
    } else {
      console.log('Invalid or empty make, hiding selects');

      // Hide all selects
      this.hideElement(modelGroup);
      this.hideElement(yearGroup);
      this.hideElement(wheelbaseGroup);
      modelSelect.value = '';
      yearSelect.value = '';
      wheelbaseSelect.value = '';
    }
  }

  handleModelChange(event) {

    const selectedModel = event.target.value;
    const selectedMakeValue = this.querySelector('#make-select').value;
    const selectedMake = this.toTitleCase(selectedMakeValue);
    const yearGroup = this.querySelector('#year-group');
    const wheelbaseGroup = this.querySelector('#wheelbase-group');
    const yearSelect = this.querySelector('#year-select');
    const wheelbaseSelect = this.querySelector('#wheelbase-select');

    // Reset vehicle found state when model changes
    this.vehicleFound = false;
    this.currentVehicleMatch = null; // Clear stored match
    this.currentRegistration = null; // Clear registration number
    this.currentVehicleData = null; // Clear vehicle data
    this.resetSideOptionPrices(); // Reset side option prices to default

    if (selectedModel && selectedMake) {
      // Get unique years for the selected make/model
      const yearsForModel = [...new Set(
        this.vehicleData
          .filter(item => item.make === selectedMake && item.range === selectedModel)
          .flatMap(item => item.years)
      )].sort((a, b) => parseInt(b) - parseInt(a)); // Sort years descending


      // Show year select and populate options
      this.populateSelectWithStrings(yearSelect, yearsForModel);
      this.showElement(yearGroup);

      // Hide wheelbase select and reset
      this.hideElement(wheelbaseGroup);
      wheelbaseSelect.value = '';
    } else {
      console.log('Hiding year and wheelbase selects');

      // Hide subsequent selects
      this.hideElement(yearGroup);
      this.hideElement(wheelbaseGroup);
      yearSelect.value = '';
      wheelbaseSelect.value = '';
    }
  }

  handleYearChange(event) {

    const selectedYear = event.target.value;
    const selectedModel = this.querySelector('#model-select').value;
    const selectedMakeValue = this.querySelector('#make-select').value;
    const selectedMake = this.toTitleCase(selectedMakeValue);
    const wheelbaseGroup = this.querySelector('#wheelbase-group');
    const wheelbaseSelect = this.querySelector('#wheelbase-select');

    // Reset vehicle found state when year changes
    this.vehicleFound = false;
    this.currentVehicleMatch = null; // Clear stored match
    this.currentRegistration = null; // Clear registration number
    this.currentVehicleData = null; // Clear vehicle data
    this.resetSideOptionPrices(); // Reset side option prices to default

    if (selectedYear && selectedModel && selectedMake) {
      // Debug: Show all matching vehicles first
      const matchingVehicles = this.vehicleData.filter(item => 
        item.make.toLowerCase() === selectedMake.toLowerCase() && 
        item.range.toLowerCase() === selectedModel.toLowerCase() && 
        (item.years.includes(selectedYear.toString()) || item.years.includes(parseInt(selectedYear)))
      );
      
      // Get unique wheelbase types for the selected make/model/year
      const wheelbaseTypes = [...new Set(
        matchingVehicles.map(item => item.wheelbase)
      )].filter(wb => wb); // Remove empty wheelbase values

      // Show wheelbase select and populate options
      this.populateSelectWithStrings(wheelbaseSelect, wheelbaseTypes);
      this.showElement(wheelbaseGroup);
    } else {
      // Hide wheelbase select
      this.hideElement(wheelbaseGroup);
      wheelbaseSelect.value = '';
    }
  }

  handleWheelbaseChange(event) {

    const selectedWheelbase = event.target.value;
    const selectedYear = this.querySelector('#year-select').value;
    const selectedModel = this.querySelector('#model-select').value;
    const selectedMakeValue = this.querySelector('#make-select').value;
    const selectedMake = this.toTitleCase(selectedMakeValue);

    // Reset vehicle found state when wheelbase changes
    this.vehicleFound = false;
    this.currentVehicleMatch = null; // Clear stored match
    this.currentRegistration = null; // Clear registration number
    this.currentVehicleData = null; // Clear vehicle data
    this.resetSideOptionPrices(); // Reset side option prices to default

    if (selectedWheelbase && selectedYear && selectedModel && selectedMake) {
      console.log('All vehicle details selected, triggering lookup:', selectedMake, selectedModel, selectedYear, selectedWheelbase);
      // Automatically trigger lookup when all required fields are selected
      this.lookupManually(selectedMake, selectedModel, selectedYear, selectedWheelbase);
    }
  }

  populateSelect(selectElement, options) {
    console.log('Populating select with options:', options);

    // Clear existing options except the first one
    const firstOption = selectElement.querySelector('option');
    selectElement.innerHTML = '';
    selectElement.appendChild(firstOption);

    // Add new options
    options.forEach(option => {
      const optionElement = document.createElement('option');
      optionElement.value = option.toLowerCase().replace(/\s+/g, '-');
      optionElement.textContent = option;
      selectElement.appendChild(optionElement);
    });

    console.log('Select populated with', options.length, 'options');
  }

  populateSelectWithStrings(selectElement, options) {
    console.log('Populating select with string options:', options);

    // Clear existing options except the first one
    const firstOption = selectElement.querySelector('option');
    selectElement.innerHTML = '';
    selectElement.appendChild(firstOption);

    // Add new options
    options.forEach(option => {
      const optionElement = document.createElement('option');
      optionElement.value = option;
      optionElement.textContent = option;
      selectElement.appendChild(optionElement);
    });

    console.log('Select populated with', options.length, 'options');
  }

  showElement(element) {
    console.log('Showing element:', element);
    element.style.display = 'block';
    // Trigger reflow for animation
    element.offsetHeight;
  }

  hideElement(element) {
    console.log('Hiding element:', element);
    element.style.display = 'none';
  }



  findVehicleMatch(apiVehicleData) {
    const { year, make, range, wheelbase } = apiVehicleData;
    
    // Find matching entry in our vehicle database
    const match = this.vehicleData.find(vehicle => {
      const isYearMatch = vehicle.years.includes(year.toString()) || vehicle.years.includes(parseInt(year));
      const isMakeMatch = vehicle.make.toLowerCase() === make.toLowerCase();
      const isRangeMatch = vehicle.range.toLowerCase() === range.toLowerCase();
      
      // If wheelbase is provided, match it exactly, otherwise just match make/model/year
      if (wheelbase) {
        return isMakeMatch && isRangeMatch && isYearMatch && 
               vehicle.wheelbase.toLowerCase() === wheelbase.toLowerCase();
      } else {
        return isMakeMatch && isRangeMatch && isYearMatch;
      }
    });

    return match;
  }

  lookupManually(make, model, year, wheelbase) {

    // Find the matching vehicle in our database to get the variant
    const vehicleMatch = this.findVehicleMatch({
      year: year,
      make: make,
      range: model,
      wheelbase: wheelbase
    });

    // Simulate processing
    setTimeout(() => {
      this.vehicleFound = true; // Mark vehicle as found
      this.currentVehicleMatch = vehicleMatch; // Store the vehicle match for side changes
      this.currentRegistration = null; // No registration number for manual lookup
      
      // Set vehicle data for form submission
      this.currentVehicleData = {
        year: year,
        make: make,
        range: model,
        wheelbase: wheelbase
      };
      // Remove the success message since there will always be a match
      // const successMessage = this.replaceMessageVariables(this.settings.success_message, vehicleData);
      // this.showMessage(successMessage, 'success');
      console.log('Manual lookup:', { make, model, year, wheelbase });
      console.log('Vehicle found state:', this.vehicleFound);

      // Find matching variant and update price display
      if (vehicleMatch) {
        // Update all side option prices for this vehicle
        this.updateSideOptionPrices(vehicleMatch);

        const variant = this.findMatchingVariant(vehicleMatch);
        if (variant) {
          this.updatePriceDisplay(variant);
          this.updateVariantId(variant.id);
          console.log('Variant found and price updated:', variant.title);
        } else {
          console.warn('No matching variant found for vehicle');
        }
      } else {
        console.warn('No vehicle match found in database');
      }
    }, 500);
  }

  // Public method to check if vehicle has been found
  hasVehicleSelected() {
    return this.vehicleFound;
  }

  // Public method to get the current registration number
  getCurrentRegistration() {
    return this.currentRegistration;
  }

  // Public method to get the current vehicle match
  getCurrentVehicleMatch() {
    return this.currentVehicleMatch;
  }

  // Public method to get the selected side/type
  getSelectedType() {
    return this.getSelectedSide();
  }

  // Public method to get vehicle data for form submission
  getVehicleFormData() {
    if (!this.vehicleFound || !this.currentVehicleMatch) {
      return null;
    }

    // For manual selection, get data from form
    const makeSelect = this.querySelector('#make-select');
    const modelSelect = this.querySelector('#model-select');
    const yearSelect = this.querySelector('#year-select');
    const wheelbaseSelect = this.querySelector('#wheelbase-select');

    if (makeSelect && modelSelect && yearSelect && wheelbaseSelect &&
        makeSelect.value && modelSelect.value && yearSelect.value && wheelbaseSelect.value) {
      return {
        make: this.toTitleCase(makeSelect.value),
        range: modelSelect.value, // Note: using 'range' to match API response
        year: yearSelect.value,
        wheelbase: wheelbaseSelect.value
      };
    }

    // Fallback to stored vehicle data
    return this.currentVehicleData || null;
  }

  showMessage(text, type = 'info') {
    // Remove existing message
    const existingMessage = this.querySelector('.registration-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create new message
    const message = document.createElement('div');
    message.className = `registration-message registration-message--${type}`;
    message.textContent = text;

    // Insert at the top of the component
    this.insertBefore(message, this.firstChild);

    // Auto-remove info messages after 3 seconds, but keep success and error messages
    if (type === 'info') {
      setTimeout(() => {
        if (message.parentNode) {
          message.remove();
        }
      }, 3000);
    }
  }
}

// Register the custom element
console.log('Registering custom element...');
customElements.define('registration-lookup', RegistrationLookup);
